import React, { useState, useEffect, useRef, memo  } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Modal from 'react-modal';
import ReservationForm from './ReservationForm';
import config from '../config';
import { bbqTerm } from '../utils/bbqTerms';
import { useIsMobile } from '../Responsive';
import { formatDateTimeGR } from './shared/dateUtils';
import { PrefsPicker, PrefsToggle, OrderPrefsPanel, parsePrefs } from './shared/PreorderNotes';
import './AssignMeats.css';

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);
const AssignMeats = ({ reservation, onCloseModal, onBeforeSave }) => {

  const [meatAssignments, setMeatAssignments] = useState([]);
  const [meats, setMeats] = useState([]);
  const [cooktypes, setCooktypes] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedOnTheSide, setSelectedOnTheSide] = useState(null);
  const [selectAllFilleting, setSelectAllFilleting] = useState(false);
  const [selectAllStandalone, setSelectAllStandalone] = useState(false);
  const [saving, setSaving] = useState(false);

  // Ιδιαίτερες προτιμήσεις (11/2026): κατάλογος, προτιμήσεις όλης της παραγγελίας, ανοιχτά panels
  const [prefCatalog, setPrefCatalog] = useState([]);
  const [orderPre, setOrderPre] = useState({ prefs: [], comment: '' });
  const [orderOpen, setOrderOpen] = useState(false);
  const [openLines, setOpenLines] = useState({});
  const keySeq = useRef(0);
  const newKey = () => `n${++keySeq.current}`;
  const toggleLine = (k) => setOpenLines((prev) => ({ ...prev, [k]: !prev[k] }));
  const setLinePrefs = (index, { prefs, comment }) => {
    setMeatAssignments((prev) => prev.map((a, i) => (i === index ? { ...a, prefs, note: comment } : a)));
  };

  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Fetch meats and cooktypes from the API
  useEffect(() => {
    axios.get(tr('{{API_BASE_URL}}/meats', {
      API_BASE_URL: config.API_BASE_URL
    }))
      .then((response) => setMeats(response.data))
      .catch((error) => console.error('Error fetching meats:', error));

    axios.get(tr('{{API_BASE_URL}}/cooktypes', {
      API_BASE_URL: config.API_BASE_URL
    }))
      .then((response) => setCooktypes(response.data))
      .catch((error) => console.error('Error fetching cook types:', error));

    axios.get(tr('{{API_BASE_URL}}/preorder-preferences', {
      API_BASE_URL: config.API_BASE_URL
    }))
      .then((response) => setPrefCatalog(Array.isArray(response.data) ? response.data : []))
      .catch((error) => console.error('Error fetching preorder preferences:', error));
  }, []);

  // Fetch assigned meats for the given reservation
  useEffect(() => {
    if (!reservation || !reservation.id) {
      console.error('Invalid reservation ID');
      return;
    }

    axios.get(tr('{{API_BASE_URL}}/assignedmeats/{{id}}', {
      API_BASE_URL: config.API_BASE_URL,
      id: reservation.id
    }))
      .then((response) => {
        const assignments = response.data.map((assignment) => ({
          ...assignment,
          _key: `id${assignment.id}`,
          actual_quantity: assignment.actual_quantity ?? assignment.grams ?? '',
          standalone: assignment.standalone?.data[0] === 1,
          ontheside: assignment.ontheside?.data[0] === 1,
          filleting: assignment.filleting?.data?.[0] === 1,
          prefs: parsePrefs(assignment.prefs),
          note: assignment.note || ''
        }));
        setSelectedOnTheSide(assignments.findIndex((assignment) => assignment.ontheside));
        setMeatAssignments(assignments.length ? assignments : [{ _key: newKey(), meat: '', cooktype: '', grams: '', actual_quantity: '', standalone: false, ontheside: false, filleting: false, prefs: [], note: '' }]);
      })
      .catch((error) => console.error('Error fetching assigned meats:', error));

    axios.get(tr('{{API_BASE_URL}}/assignedmeats/{{id}}/preorder', {
      API_BASE_URL: config.API_BASE_URL,
      id: reservation.id
    }))
      .then((response) => setOrderPre({
        prefs: parsePrefs(response.data?.prefs),
        comment: response.data?.comment || ''
      }))
      .catch((error) => console.error('Error fetching preorder preferences:', error));
  }, [reservation]);

  const handleToggleAll = (field, value) => {
    const updated = meatAssignments.map(a => ({ ...a, [field]: value }));
    setMeatAssignments(updated);
    
    if (field === 'filleting') setSelectAllFilleting(value);
    if (field === 'standalone') setSelectAllStandalone(value);
  };

  useEffect(() => {
    // Update selectAllFilleting state
    setSelectAllFilleting(meatAssignments.length == meatAssignments.filter(a => a.filleting).length);
    // Update selectAllStandalone state
    const leftOvers = meatAssignments.filter(a => !a.standalone);
    if(meatAssignments.length>1 && leftOvers.length == 1){
      setTimeout(()=>{leftOvers[0].standalone = true},0);
    }
    setTimeout(()=>{setSelectAllStandalone(meatAssignments.length == meatAssignments.filter(a => a.standalone).length)},100);
  }, [meatAssignments]);

  const audit = React.useMemo(() => {
    const existing = meatAssignments.find(a => a.id);
    if (existing) {
      return {
        created: existing.created,
        created_by_username: existing.created_by_username,
        updated: existing.updated,
        updated_by_username: existing.updated_by_username
      };
    }
    // Fallback to reservation-level audit if available
    if (reservation) {
      return {
        created: reservation.created,
        created_by_username: reservation.created_by_username,
        updated: reservation.updated,
        updated_by_username: reservation.updated_by_username
      };
    }
    return null;
  }, [meatAssignments, reservation]);

  // Handle input field changes
  const handleChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const updatedAssignments = [...meatAssignments];
    const previousAssignment = { ...updatedAssignments[index] };
    const nextValue = type === 'checkbox' ? checked : value;

    updatedAssignments[index][name] = nextValue;

    if (name === 'grams') {
      const previousRequested = String(previousAssignment.grams ?? '');
      const previousActual = String(previousAssignment.actual_quantity ?? '');
      if (!previousActual || previousActual === previousRequested) {
        updatedAssignments[index].actual_quantity = nextValue;
      }
    }
  
    // Auto-fill defaults when selecting a meat (or clear if no defaults)
    if (name === 'meat') {
      const selectedMeat = meats.find((m) => String(m.id) === String(value));
    
      if (!selectedMeat) {
        // meat cleared or not found -> clear fields
        updatedAssignments[index].grams = '';
        updatedAssignments[index].actual_quantity = '';
        updatedAssignments[index].cooktype = '';
      } else {
        // grams
        if (selectedMeat.default_quantity !== null && selectedMeat.default_quantity !== undefined && selectedMeat.default_quantity !== '') {
          updatedAssignments[index].grams = String(selectedMeat.default_quantity);
          updatedAssignments[index].actual_quantity = String(selectedMeat.default_quantity);
        } else {
          updatedAssignments[index].grams = '';
          updatedAssignments[index].actual_quantity = '';
        }
    
        // cooktype
        if (selectedMeat.default_cooktype !== null && selectedMeat.default_cooktype !== undefined && selectedMeat.default_cooktype !== '') {
          updatedAssignments[index].cooktype = String(selectedMeat.default_cooktype);
        } else {
          updatedAssignments[index].cooktype = '';
        }
      }
    }

    setMeatAssignments(updatedAssignments);
  };


  const check4leftOver = () => {
    //if(meatAssignments.length>1 && meatAssignments.filter(a => !a.standalone).length == 1)
    //  setSelectAllStandalone(true);
  }

  const handleOnTheSideChange = (index) => {
    const isSelected = selectedOnTheSide === index;
    setSelectedOnTheSide(isSelected ? null : index);
    const updatedAssignments = meatAssignments.map((assignment, i) => ({
      ...assignment,
      ontheside: !isSelected && i === index,
    }));
    setMeatAssignments(updatedAssignments);
  };

  const handleAddMeatAssignment = () => {
    setMeatAssignments([
      ...meatAssignments,
      { _key: newKey(), meat: '', cooktype: '', grams: '', actual_quantity: '', standalone: false, ontheside: false, filleting: false, prefs: [], note: '' },
    ]);
  };

  const handleDelete = (id, index) => {
    if (id) {
      if(!window.confirm(bbqTerm('Delete assigned meat #{{n}} ?', { n: index + 1 })))
        return;    
      axios.delete(tr('{{API_BASE_URL}}/assignedmeats/{{id}}', {
        API_BASE_URL: config.API_BASE_URL,
        id
      }))
        .then(() => {
          setMeatAssignments((prev) => prev.filter((assignment) => assignment.id !== id));
        })
        .catch(() => setError('Error deleting assignment.'));
    } else {
      setMeatAssignments((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!reservation || !reservation.id) {
        setError('No reservation ID provided.');
        return;
    }

    const data = {
        reservation: reservation.id,
        assignments: meatAssignments.map((assignment) => ({
            id: assignment.id || null, // Include ID if it exists
            meat: assignment.meat,
            cooktype: assignment.cooktype,
            grams: assignment.grams,
            actual_quantity: assignment.actual_quantity || assignment.grams,
            standalone: assignment.standalone,
            ontheside: assignment.ontheside,
            filleting: assignment.filleting,
            prefs: assignment.prefs || [],
            note: assignment.note || ''
        })),
        // Προτιμήσεις + σχόλιο ΟΛΗΣ της προπαραγγελίας
        preorder: { prefs: orderPre.prefs, comment: orderPre.comment },
    };

    try {
      setSaving(true);
      // 1) Persist reservation details (people, status/state, order, comment) first
      if (typeof onBeforeSave === 'function') {
        await onBeforeSave(); // if this throws, meats won't be saved
      }
      // 2) Then save meats
      await axios.post(tr('{{API_BASE_URL}}/assignedmeats/bulk', {
        API_BASE_URL: config.API_BASE_URL
      }), data);
      setMessage('Assignments saved successfully.');
      setError('');
      if (onCloseModal) {
        setTimeout(onCloseModal, 1000);
      } else {
        setTimeout(() => navigate("/reservations-list"), 1000);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Error saving assignments.');
    } finally {
      setSaving(false);
    }
  };

  // ---------- κοινά κομμάτια UI (ανανέωση 11/2026) ----------
  const canDelete = meatAssignments.length > 1 || !!meatAssignments[0]?.meat;
  const multi = meatAssignments.length > 1;

  const TrashIcon = () => (
    <svg className="am-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" />
    </svg>
  );
  const PlusIcon = () => (
    <svg className="am-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
  );
  const SaveIcon = () => (
    <svg className="am-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
  );

  const auditLine = audit && (audit.created || audit.updated) ? (
    <div className="am-audit">
      {audit.created && (
        <span>
          <b>{tr('Created:')}</b> {formatDateTimeGR(audit.created)}
          {audit.created_by_username ? tr(' · {{created_by_username}}', { created_by_username: audit.created_by_username }) : ''}
        </span>
      )}
      {audit.updated && (
        <span>
          <b>{tr('Updated:')}</b> {formatDateTimeGR(audit.updated)}
          {audit.updated_by_username ? tr(' · {{updated_by_username}}', { updated_by_username: audit.updated_by_username }) : ''}
        </span>
      )}
    </div>
  ) : null;

  const alerts = (
    <>
      {error && <div className="am-alert am-alert--err" role="alert">{error}</div>}
      {message && <div className="am-alert am-alert--ok" role="status">{message}</div>}
    </>
  );

  const saveButton = (label) => (
    <button type="submit" disabled={saving} className="am-btn am-btn--primary">
      <SaveIcon /> {saving ? 'Saving…' : label}
    </button>
  );

  const addButton = (
    <button type="button" className="am-add" onClick={handleAddMeatAssignment}>
      <PlusIcon /> {tr('Add')}
    </button>
  );

  const orderPanel = (
    <OrderPrefsPanel open={orderOpen} onToggle={() => setOrderOpen((v) => !v)}
      catalog={prefCatalog} value={orderPre} onChange={setOrderPre} />
  );

  const meatOptions = (
    <>
      <option value="" disabled>{bbqTerm('Select Meat')}</option>
      {meats.map((meat) => <option key={meat.id} value={meat.id}>{meat.name}</option>)}
    </>
  );
  const cooktypeOptions = (
    <>
      <option value="" disabled>{tr('Select Cook Type')}</option>
      {cooktypes.map((cooktype) => <option key={cooktype.id} value={cooktype.id}>{cooktype.name}</option>)}
    </>
  );

  // ---------- MOBILE UI (cards) ----------
  if (isMobile) {
    return (
      <div className="am am--mobile">
        <form onSubmit={handleSubmit}>
          <div className="am-header">
            <div>
              <h2 className="am-title">{bbqTerm('Assign Meats')}</h2>
              {auditLine}
            </div>
            {saveButton('Save')}
          </div>
          {alerts}

          {meatAssignments.map((assignment, index) => {
            const open = !!openLines[assignment._key];
            return (
              <div key={assignment._key || assignment.id || index} className={`am-card${open ? ' is-open' : ''}`}>
                <div className="am-card-head">
                  <span className="am-index">#{index + 1}</span>
                  <select name="meat" className="am-input am-grow" value={assignment.meat}
                    onChange={(e) => handleChange(index, e)} required aria-label={bbqTerm('Meat')}>
                    {meatOptions}
                  </select>
                  {canDelete && (
                    <button type="button" className="am-icon-btn" onClick={() => handleDelete(assignment.id, index)}
                      aria-label={tr('Remove item')} title={tr('Remove')}>
                      <TrashIcon />
                    </button>
                  )}
                </div>

                <div className="am-grid3">
                  <label className="am-field">
                    <span className="am-label">{tr('Requested Qty/ppl')}</span>
                    <input type="number" name="grams" className="am-input" value={assignment.grams}
                      onChange={(e) => handleChange(index, e)} min="1" step="1" required />
                  </label>
                  <label className="am-field">
                    <span className="am-label">{tr('Actual Qty')}</span>
                    <input type="number" name="actual_quantity" className="am-input"
                      value={assignment.actual_quantity ?? assignment.grams}
                      onChange={(e) => handleChange(index, e)} min="1" step="1" required />
                  </label>
                  <label className="am-field">
                    <span className="am-label">{tr('Cook Type')}</span>
                    <select name="cooktype" className="am-input" value={assignment.cooktype}
                      onChange={(e) => handleChange(index, e)} required>
                      {cooktypeOptions}
                    </select>
                  </label>
                </div>

                <div className="am-checks">
                  {multi && (
                    <label className="am-check">
                      <input type="checkbox" name="standalone" checked={assignment.standalone}
                        onChange={(e) => { handleChange(index, e); }} />
                      {tr('Standalone')}
                    </label>
                  )}
                  <label className="am-check">
                    <input type="radio" name="ontheside" checked={selectedOnTheSide === index}
                      onChange={() => handleOnTheSideChange(index)} />
                    {tr('On the side')}
                  </label>
                  <label className="am-check">
                    <input type="checkbox" name="filleting" checked={assignment.filleting}
                      onChange={(e) => handleChange(index, e)} />
                    {tr('Filleting')}
                  </label>
                  <span className="am-spacer" />
                  <PrefsToggle open={open} count={(assignment.prefs || []).length}
                    hasComment={!!String(assignment.note || '').trim()} onClick={() => toggleLine(assignment._key)} />
                </div>

                {open && (
                  <div className="am-card-prefs">
                    <PrefsPicker catalog={prefCatalog} prefs={assignment.prefs || []} comment={assignment.note || ''}
                      onChange={(v) => setLinePrefs(index, v)} />
                  </div>
                )}
              </div>
            );
          })}

          {addButton}
          {orderPanel}
        </form>
      </div>
    );
  }

  // ---------- DESKTOP UI (table) ----------
  return (
    <div className="am">
      <form onSubmit={handleSubmit}>
        <div className="am-header">
          <div>
            <h2 className="am-title">{bbqTerm('Assign Meats and Cook Types')}</h2>
            {auditLine}
          </div>
          {saveButton('Save Assignments')}
        </div>
        {alerts}

        <div className="am-table-wrap">
          <table className="am-table">
            <thead>
              <tr>
                <th className="am-left am-col-meat">{bbqTerm('Meat')}</th>
                <th>{tr('Requested Qty/ppl')}</th>
                <th>{tr('Actual Qty')}</th>
                <th className="am-left am-col-cook">{tr('Cook Type')}</th>
                <th>
                  <label className="am-th-check">
                    {multi && (
                      <input type="checkbox" checked={selectAllStandalone}
                        onChange={(e) => handleToggleAll('standalone', e.target.checked)} />
                    )}
                    {tr('Stand Alone')}
                  </label>
                </th>
                <th>{tr('On the Side')}</th>
                <th>
                  <label className="am-th-check">
                    {multi && (
                      <input type="checkbox" checked={selectAllFilleting}
                        onChange={(e) => handleToggleAll('filleting', e.target.checked)} />
                    )}
                    {tr('Filleting')}
                  </label>
                </th>
                <th>{tr('Preferences')}</th>
                <th aria-label={tr('Remove')} />
              </tr>
            </thead>
            <tbody>
              {meatAssignments.map((assignment, index) => {
                const open = !!openLines[assignment._key];
                return (
                  <React.Fragment key={assignment._key || assignment.id || index}>
                    <tr className={`am-row${open ? ' is-open' : ''}`}>
                      <td className="am-left">
                        <select name="meat" className="am-input" value={assignment.meat}
                          onChange={(e) => handleChange(index, e)} required aria-label={bbqTerm('Meat')}>
                          {meatOptions}
                        </select>
                      </td>
                      <td>
                        <input type="number" name="grams" className="am-input am-input--num" value={assignment.grams}
                          onChange={(e) => handleChange(index, e)} min="1" step="1" required
                          aria-label={tr('Requested Qty/ppl')} />
                      </td>
                      <td>
                        <input type="number" name="actual_quantity" className="am-input am-input--num"
                          value={assignment.actual_quantity ?? assignment.grams}
                          onChange={(e) => handleChange(index, e)} min="1" step="1" required
                          aria-label={tr('Actual Qty')} />
                      </td>
                      <td className="am-left">
                        <select name="cooktype" className="am-input" value={assignment.cooktype}
                          onChange={(e) => handleChange(index, e)} required aria-label={tr('Cook Type')}>
                          {cooktypeOptions}
                        </select>
                      </td>
                      <td>
                        {multi && (
                          <input type="checkbox" name="standalone" checked={assignment.standalone}
                            onChange={(e) => { handleChange(index, e); }} aria-label={tr('Stand Alone')} />
                        )}
                      </td>
                      <td>
                        <input type="radio" name="ontheside" checked={selectedOnTheSide === index}
                          onClick={() => handleOnTheSideChange(index)}
                          onChange={() => handleOnTheSideChange(index)} aria-label={tr('On the Side')} />
                      </td>
                      <td>
                        <input type="checkbox" name="filleting" checked={assignment.filleting}
                          onChange={(e) => handleChange(index, e)} aria-label={tr('Filleting')} />
                      </td>
                      <td>
                        <PrefsToggle open={open} count={(assignment.prefs || []).length}
                          hasComment={!!String(assignment.note || '').trim()} onClick={() => toggleLine(assignment._key)} />
                      </td>
                      <td>
                        {canDelete && (
                          <button type="button" className="am-icon-btn" onClick={() => handleDelete(assignment.id, index)}
                            aria-label={tr('Remove item')} title={tr('Remove')}>
                            <TrashIcon />
                          </button>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr className="am-prefs-row">
                        <td colSpan={9}>
                          <div className="am-prefs-inner">
                            <PrefsPicker catalog={prefCatalog} prefs={assignment.prefs || []} comment={assignment.note || ''}
                              onChange={(v) => setLinePrefs(index, v)} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {addButton}
        {orderPanel}
      </form>
    </div>
  );
};

export default memo(AssignMeats);
